//Question Slice
import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { IAuthResponse } from '../auth/IAuthState';
import { createEstablishmentTypeQuestion, deleteEstablishmentTypeQuestion, fetchEstablishmentTypeQuestions, updateEstablishmentTypeQuestion } from './establishmentTypeQuestionAsyncThunk';
import { toast } from 'react-toastify';
import {EstablishmentTypeQuestion, EstablishmentType} from '../../../types';

export interface CreateEstablishmentTypeQuestionRequest {
    establishmentType: string;
    name: string[];
}

// Request body for updating a question
export interface UpdateEstablishmentTypeQuestionRequest {
    name: string;
    establishmentType: string;
}

export interface DeleteEstablishmentTypeQuestion extends IAuthResponse {
    id: string;
}

interface establishmentTypeQuestionState {
    data: EstablishmentTypeQuestion[];
    selectedEstablishmentType: EstablishmentType | null;
    total: number; // Total records for pagination
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
    loading: boolean;
    isModalOpen: boolean;
    isEdit: boolean;
    editingRecord: EstablishmentTypeQuestion | null;
}

const initialState: establishmentTypeQuestionState = {
    data: [],
    total: 0, // Initialize total records
    status: 'idle',
    selectedEstablishmentType: null,
    error: null,
    loading: false,
    isModalOpen: false,
    isEdit: false,
    editingRecord: null,
};

// Slice
const establishmentTypeQuestionSlice = createSlice({
    name: 'establishmentTypeQuestion',
    initialState,
    reducers: {
        setModalOpen: (state, action) => {
            state.isModalOpen = action.payload;
        },
        setEdit: (state, action) => {
            state.isEdit = action.payload;
        },
        setEditingRecord: (state, action) => {
            state.editingRecord = action.payload;
        },
        setQuestionData: (state, action) => {
            state.data = action.payload;
        },
        setSelectedEstablishmentType: (state, action) => {
            state.selectedEstablishmentType = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
        // Fetch
            .addCase(fetchEstablishmentTypeQuestions.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchEstablishmentTypeQuestions.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = [...action.payload.data];
                state.total = action.payload.total;
            })
            .addCase(fetchEstablishmentTypeQuestions.rejected, (state, action) => {
                state.status = 'failed';
                toast.error(action.payload || 'Failed to fetch EstablishmentTypeQuestion');
                state.error = action.payload || 'Failed to fetch EstablishmentTypeQuestion';
            })
            // Create
            .addCase(createEstablishmentTypeQuestion.pending, (state) => {
                state.loading = true;
            })
            .addCase(createEstablishmentTypeQuestion.fulfilled, (state, action: any) => {
                state.loading = false;
                state.total = state.total + 1;
                if (state.selectedEstablishmentType?.id === action?.payload?.establishmentType)
                    state.data = [...state.data, action.payload];
            })
            .addCase(createEstablishmentTypeQuestion.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to create EstablishmentTypeQuestion';
            })
            // Update
            .addCase(updateEstablishmentTypeQuestion.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateEstablishmentTypeQuestion.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
            })
            .addCase(updateEstablishmentTypeQuestion.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to update EstablishmentTypeQuestion');
                state.error = action.payload || 'Failed to update EstablishmentTypeQuestion';
            })
            // Delete
            .addCase(deleteEstablishmentTypeQuestion.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteEstablishmentTypeQuestion.fulfilled, (state, action) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data = state.data.filter((item) => item.id !== action.payload.id);
                    state.total = state.total - 1;
                }
                state.loading = false;
            })
            .addCase(deleteEstablishmentTypeQuestion.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to delete EstablishmentTypeQuestion');
                state.error = action.payload || 'Failed to delete EstablishmentTypeQuestion';
            });
    },
});

export const { setModalOpen, setEdit, setEditingRecord, setQuestionData, setSelectedEstablishmentType } = establishmentTypeQuestionSlice.actions;

export const selectEstablishmentTypeQuestion = (state: RootState) => state.establishmentTypeQuestion;

export default establishmentTypeQuestionSlice.reducer;
